/**
 * TASK POSITION CALCULATION HELPERS
 *
 * This module contains pure functions for calculating task positions during
 * reordering operations. These functions handle the complex logic of determining
 * where to place a task when moving within or between columns.
 *
 * Key Features:
 * - Pure functions with no side effects
 * - Comprehensive position calculation for all scenarios
 * - Sparse numbering system for efficient reordering
 * - Conflict detection and resolution
 * - Support for both within-column and cross-column moves
 */

import { TaskData, FormattedTask } from '../taskTypes';
import { TaskItem } from '../../../types/topic';

/**
 * Configuration constants for position calculation
 */
export const POSITION_CONSTANTS = {
  /** Default spacing between task positions (sparse numbering) */
  POSITION_STEP: 1000,
  /** Minimum gap required between positions before rebalancing */
  MIN_POSITION_GAP: 100,
  /** Default position for first task in empty column */
  DEFAULT_POSITION: 1000,
} as const;

/**
 * Result of a position calculation operation
 */
export interface PositionCalculationResult {
  /** The calculated target position for the task */
  targetPosition: number;
  /** Whether the positions in the column need rebalancing */
  needsRebalancing: boolean;
  /** Reason for rebalancing (if needed) */
  rebalanceReason?: 'position_conflict' | 'gap_too_small' | 'negative_position';
}

/**
 * Input parameters for position calculation
 */
export interface PositionCalculationInput {
  /** Index where the task should be inserted (0-based) */
  targetIndex: number;
  /** All tasks currently in the target column, sorted by position */
  tasksInColumn: TaskData[];
  /** Whether this is a cross-column move */
  isCrossColumnMove: boolean;
}

/**
 * Calculates the optimal position for a task being moved to a new index
 *
 * This function implements a sparse numbering system where tasks are positioned
 * at multiples of POSITION_STEP (1000). This allows for efficient insertions
 * without requiring frequent position updates of other tasks.
 *
 * @param input - The position calculation parameters
 * @returns Position calculation result with target position and rebalancing info
 */
export function calculateTaskPosition(input: PositionCalculationInput): PositionCalculationResult {
  const { targetIndex, tasksInColumn } = input;

  // Validate input
  if (targetIndex < 0) {
    throw new Error('Target index cannot be negative');
  }

  if (!Array.isArray(tasksInColumn)) {
    throw new Error('Tasks in column must be an array');
  }

  // Ensure tasks are sorted by position
  const sortedTasks = [...tasksInColumn].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Case 1: Empty column
  if (sortedTasks.length === 0) {
    return {
      targetPosition: POSITION_CONSTANTS.DEFAULT_POSITION,
      needsRebalancing: false,
    };
  }

  // Case 2: Insert at the beginning (index 0)
  if (targetIndex === 0) {
    const firstTask = sortedTasks[0];
    const firstPosition = firstTask.position || 0;

    // Try to place before first task
    const targetPosition = firstPosition - POSITION_CONSTANTS.POSITION_STEP;

    // Check if position would be negative or too close
    if (
      targetPosition <= 0 ||
      firstPosition - targetPosition < POSITION_CONSTANTS.MIN_POSITION_GAP
    ) {
      return {
        targetPosition: firstPosition - 10, // Temporary position
        needsRebalancing: true,
        rebalanceReason: targetPosition <= 0 ? 'negative_position' : 'gap_too_small',
      };
    }

    return {
      targetPosition,
      needsRebalancing: false,
    };
  }

  // Case 3: Insert at the end
  if (targetIndex >= sortedTasks.length) {
    const lastTask = sortedTasks[sortedTasks.length - 1];
    const lastPosition = lastTask.position || 0;
    const targetPosition = lastPosition + POSITION_CONSTANTS.POSITION_STEP;

    return {
      targetPosition,
      needsRebalancing: false,
    };
  }

  // Case 4: Insert in the middle
  const beforeTask = sortedTasks[targetIndex - 1];
  const afterTask = sortedTasks[targetIndex];

  const beforePosition = beforeTask.position || 0;
  const afterPosition = afterTask.position || 0;

  // Check if there's enough space between the two positions
  const gap = afterPosition - beforePosition;

  if (gap < POSITION_CONSTANTS.MIN_POSITION_GAP) {
    // Not enough space, needs rebalancing
    const targetPosition = beforePosition + Math.floor(gap / 2);

    return {
      targetPosition,
      needsRebalancing: true,
      rebalanceReason: 'gap_too_small',
    };
  }

  // Calculate midpoint position
  const targetPosition = beforePosition + Math.floor(gap / 2);

  // Check for position conflicts (unlikely but possible with concurrent updates)
  const hasConflict = sortedTasks.some(task => task.position === targetPosition);

  if (hasConflict) {
    return {
      targetPosition: beforePosition + 10, // Temporary position
      needsRebalancing: true,
      rebalanceReason: 'position_conflict',
    };
  }

  return {
    targetPosition,
    needsRebalancing: false,
  };
}

/**
 * Optimistically updates task positions in a FormattedTask array for UI updates
 *
 * This function is used for optimistic updates in the UI before server confirmation.
 * It handles both within-column reordering and cross-column moves.
 *
 * @param tasks - Current task list
 * @param taskId - ID of task being moved
 * @param newStatus - New status (for cross-column moves)
 * @param targetIndex - Target index in the new column
 * @returns Updated task array with new positions
 */
export function updateTaskPositionsOptimistically(
  tasks: FormattedTask[],
  taskId: string,
  newStatus: TaskItem['status'] | undefined,
  targetIndex: number
): FormattedTask[] {
  if (!tasks || !Array.isArray(tasks)) {
    return tasks;
  }

  // Find the task being moved
  const taskToMove = tasks.find(t => t.id === taskId);
  if (!taskToMove) {
    return tasks;
  }

  const originalStatus = taskToMove.status;

  // Handle cross-column move
  if (newStatus && newStatus !== originalStatus) {
    return handleCrossColumnMove(tasks, taskToMove, newStatus, targetIndex);
  }

  // Handle within-column move
  return handleWithinColumnMove(tasks, taskToMove, targetIndex);
}

/**
 * Handles cross-column task moves with position recalculation
 */
function handleCrossColumnMove(
  tasks: FormattedTask[],
  taskToMove: FormattedTask,
  newStatus: TaskItem['status'],
  targetIndex: number
): FormattedTask[] {
  // Remove task from original position
  const withoutTask = tasks.filter(t => t.id !== taskToMove.id);

  // Get tasks in target column
  const tasksInTargetColumn = withoutTask
    .filter(t => t.status === newStatus)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  // Update task with new status
  const updatedTask = { ...taskToMove, status: newStatus };

  // Insert at target index
  tasksInTargetColumn.splice(targetIndex, 0, updatedTask);

  // Recalculate positions using sparse numbering
  const updatedTargetTasks = tasksInTargetColumn.map((task, index) => ({
    ...task,
    position: (index + 1) * POSITION_CONSTANTS.POSITION_STEP,
  }));

  // Combine with tasks from other columns
  const tasksInOtherColumns = withoutTask.filter(t => t.status !== newStatus);

  return [...tasksInOtherColumns, ...updatedTargetTasks];
}

/**
 * Handles within-column task moves with position recalculation
 */
function handleWithinColumnMove(
  tasks: FormattedTask[],
  taskToMove: FormattedTask,
  targetIndex: number
): FormattedTask[] {
  // Get tasks in the same column
  const tasksInColumn = tasks
    .filter(t => t.status === taskToMove.status)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  const fromIndex = tasksInColumn.findIndex(t => t.id === taskToMove.id);

  // No change if moving to same position
  if (fromIndex === -1 || fromIndex === targetIndex) {
    return tasks;
  }

  // Create new order
  const reorderedTasks = [...tasksInColumn];
  reorderedTasks.splice(fromIndex, 1); // Remove task
  reorderedTasks.splice(targetIndex, 0, taskToMove); // Insert at new position

  // Update positions with sparse numbering
  const withNewPositions = reorderedTasks.map((task, index) => ({
    ...task,
    position: (index + 1) * POSITION_CONSTANTS.POSITION_STEP,
  }));

  // Combine with tasks from other columns
  const tasksInOtherColumns = tasks.filter(t => t.status !== taskToMove.status);

  return [...tasksInOtherColumns, ...withNewPositions];
}

/**
 * Validates that a set of tasks have properly ordered positions
 *
 * @param tasks - Tasks to validate
 * @returns Validation result with any issues found
 */
export interface PositionValidationResult {
  isValid: boolean;
  issues: Array<{
    type: 'duplicate_position' | 'negative_position' | 'gap_too_small' | 'unsorted';
    taskIds: string[];
    positions: number[];
    message: string;
  }>;
}

export function validateTaskPositions(tasks: TaskData[]): PositionValidationResult {
  const issues: PositionValidationResult['issues'] = [];

  if (!tasks || tasks.length === 0) {
    return { isValid: true, issues: [] };
  }

  // Sort tasks by position for validation
  const sortedTasks = [...tasks].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Check for negative positions
  const negativePositions = sortedTasks.filter(task => (task.position || 0) <= 0);
  if (negativePositions.length > 0) {
    issues.push({
      type: 'negative_position',
      taskIds: negativePositions.map(t => t.id),
      positions: negativePositions.map(t => t.position || 0),
      message: `${negativePositions.length} task(s) have negative or zero positions`,
    });
  }

  // Check for duplicate positions
  const positionGroups = new Map<number, TaskData[]>();
  sortedTasks.forEach(task => {
    const pos = task.position || 0;
    if (!positionGroups.has(pos)) {
      positionGroups.set(pos, []);
    }
    positionGroups.get(pos)!.push(task);
  });

  const duplicates = Array.from(positionGroups.entries()).filter(([_, tasks]) => tasks.length > 1);
  if (duplicates.length > 0) {
    duplicates.forEach(([position, duplicateTasks]) => {
      issues.push({
        type: 'duplicate_position',
        taskIds: duplicateTasks.map(t => t.id),
        positions: [position],
        message: `${duplicateTasks.length} tasks share position ${position}`,
      });
    });
  }

  // Check for gaps that are too small
  for (let i = 0; i < sortedTasks.length - 1; i++) {
    const currentPos = sortedTasks[i].position || 0;
    const nextPos = sortedTasks[i + 1].position || 0;
    const gap = nextPos - currentPos;

    if (gap > 0 && gap < POSITION_CONSTANTS.MIN_POSITION_GAP) {
      issues.push({
        type: 'gap_too_small',
        taskIds: [sortedTasks[i].id, sortedTasks[i + 1].id],
        positions: [currentPos, nextPos],
        message: `Gap between positions ${currentPos} and ${nextPos} is too small (${gap})`,
      });
    }
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
}

/**
 * Calculates optimal positions for rebalancing a column
 *
 * @param tasks - Tasks in the column to rebalance
 * @param positionStep - Step size between positions (default: 1000)
 * @returns Array of tasks with new positions
 */
export function calculateRebalancedPositions(
  tasks: TaskData[],
  positionStep: number = POSITION_CONSTANTS.POSITION_STEP
): Array<TaskData & { position: number }> {
  if (!tasks || tasks.length === 0) {
    return [];
  }

  // Sort tasks by current position
  const sortedTasks = [...tasks].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Assign new positions with proper spacing
  return sortedTasks.map((task, index) => ({
    ...task,
    position: (index + 1) * positionStep,
  }));
}
